import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { adminListOrders, adminUpdateOrderStatus } from "@/lib/admin-dashboard.functions";
import { VoysCallButton } from "@/components/voys/VoysCallButton";

const STATUSES = ["pending", "processing", "completed", "cancelled"] as const;
type Status = (typeof STATUSES)[number];

export const Route = createFileRoute("/_authenticated/admin/orders")({
  head: () => ({ meta: [{ title: "Admin — Bestellingen" }, { name: "robots", content: "noindex" }] }),
  component: AdminOrders,
});

function AdminOrders() {
  const { isAdmin, loading } = useAuth();
  const list = useServerFn(adminListOrders);
  const upd = useServerFn(adminUpdateOrderStatus);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-orders"],
    queryFn: () => list({}),
    enabled: isAdmin,
  });

  const updMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Status }) => upd({ data: { id, status } }),
    onSuccess: () => {
      toast.success("Status bijgewerkt");
      qc.invalidateQueries({ queryKey: ["admin-orders"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (loading) return <div className="p-8">Laden…</div>;
  if (!isAdmin) return <div className="p-8">Geen toegang.</div>;

  const orders = data?.orders ?? [];

  return (
    <div className="p-6 space-y-4 max-w-7xl mx-auto">
      <div>
        <h1 className="text-3xl font-bold">Bestellingen</h1>
        <p className="text-muted-foreground">{orders.length} (laatste 200)</p>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="text-left p-2">Nummer</th>
              <th className="text-left p-2">Datum</th>
              <th className="text-left p-2">Telefoon</th>
              <th className="text-right p-2">Totaal</th>
              <th className="text-left p-2">Status</th>
              <th className="text-right p-2">Acties</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">Laden…</td></tr>
            ) : orders.length === 0 ? (
              <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">Geen bestellingen</td></tr>
            ) : orders.map((o: any) => (
              <tr key={o.id} className="border-t">
                <td className="p-2 font-mono">{o.order_number}</td>
                <td className="p-2">{new Date(o.created_at).toLocaleString("nl-NL")}</td>
                <td className="p-2">
                  <div className="flex items-center gap-2">
                    {o.phone ? (
                      <>
                        <a href={`tel:${o.phone}`} className="text-primary hover:underline text-sm">{o.phone}</a>
                        <VoysCallButton phone={o.phone} size="xs" showLabel={false} />
                      </>
                    ) : (
                      <span className="text-muted-foreground text-sm">—</span>
                    )}
                  </div>
                </td>
                <td className="p-2 text-right">€{(o.total_cents / 100).toFixed(2)}</td>
                <td className="p-2">
                  <select
                    value={o.status}
                    onChange={(e) => updMut.mutate({ id: o.id, status: e.target.value as Status })}
                    className="border rounded px-2 py-1 text-sm bg-background"
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="p-2 text-right">
                  <Link to="/account/bestellingen/$id" params={{ id: o.id }} className="text-primary text-sm hover:underline">Bekijk</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
